import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Lang } from "@/i18n";
import { path } from "@/lib/site";

export type CtaLinkType = "internal" | "external";

const INTERNAL_KEYS = [
  "home",
  "services",
  "references",
  "about",
  "faq",
  "tools",
  "contact",
  "cookies",
] as const;
type InternalKey = (typeof INTERNAL_KEYS)[number];

const COOKIES_PATH: Record<Lang, string> = {
  fr: "/cookies",
  en: "/en/cookies",
  nl: "/nl/cookies",
};

function isInternalKey(s: string): s is InternalKey {
  return (INTERNAL_KEYS as readonly string[]).includes(s);
}

export function resolveCtaTarget(
  linkType: CtaLinkType,
  target: string,
  lang: Lang,
): string {
  if (linkType === "external") return target;
  if (target === "cookies") return COOKIES_PATH[lang];
  if (isInternalKey(target) && target !== "cookies") {
    return path(lang, target as Exclude<InternalKey, "cookies">);
  }
  return target.startsWith("/") ? target : `/${target}`;
}

const pick = (v: unknown, lang: Lang): string => {
  if (v && typeof v === "object" && lang in (v as object)) {
    const s = (v as Record<string, unknown>)[lang];
    return typeof s === "string" ? s : "";
  }
  return "";
};

export interface ResolvedCta {
  label: string;
  href: string;
  isExternal: boolean;
  openInNewTab: boolean;
  /** Raw config in case caller wants to know */
  linkType: CtaLinkType;
  target: string;
}

export function useHeaderCta(lang: Lang, fallbackLabel: string) {
  return useQuery({
    queryKey: ["public", "site_cta", lang],
    queryFn: async (): Promise<ResolvedCta> => {
      type CtaRow = {
        cta_label: unknown;
        cta_link_type: CtaLinkType | null;
        cta_target: string | null;
        cta_open_in_new_tab: boolean | null;
      };
      const { data, error } = await (supabase as unknown as {
        from: (t: string) => {
          select: (cols: string) => { maybeSingle: () => Promise<{ data: CtaRow | null; error: unknown }> };
        };
      })
        .from("site_settings_public")
        .select("cta_label, cta_link_type, cta_target, cta_open_in_new_tab")
        .maybeSingle();
      if (error) throw error;
      const linkType = ((data?.cta_link_type as CtaLinkType) ?? "internal");
      const target = data?.cta_target ?? "contact";
      const label = pick(data?.cta_label, lang) || pick(data?.cta_label, "fr") || fallbackLabel;
      return {
        label,
        href: resolveCtaTarget(linkType, target, lang),
        isExternal: linkType === "external",
        openInNewTab: !!data?.cta_open_in_new_tab,
        linkType,
        target,
      };
    },
    staleTime: 60_000,
  });
}

export const CTA_INTERNAL_KEYS = INTERNAL_KEYS;
